import { useMemo } from 'react';
import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import {
    actionsColumn,
    DataTable,
    type DataTableAction,
    type DataTableColumn,
    type DataTableSearchConfig
} from '@odo/shared-ui';
import { UserCell } from '../../users/UserCell';
import { formatCreatedDate } from '../../../../utils/dates';
import type { OrthodontistUserRow } from '../../../../services/orthodontistAccounts';

const TD_PADDING = 'px-3 first:pl-5 last:pr-4';
const TH_PADDING = 'px-3 first:pl-5 last:pr-4';
const ROW_AVATAR_SIZE = 32;
const SKELETON_ROW_COUNT = 4;

const MEMBERSHIP_LABELS: Record<OrthodontistUserRow['membershipType'], string> = {
    member: 'Member',
    odo_impersonator: 'ODO (impersonator)'
};

function SkeletonRows() {
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-16 border-b border-gray-200">
                    <td className={`${TD_PADDING} align-middle`}>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                            <div className="h-3.5 w-32 rounded bg-gray-200 animate-pulse" />
                        </div>
                    </td>
                    <td className={`${TD_PADDING} align-middle`}>
                        <div className="h-3.5 w-20 rounded bg-gray-200 animate-pulse" />
                    </td>
                    <td className={`${TD_PADDING} align-middle`}>
                        <div className="h-3.5 w-16 rounded bg-gray-200 animate-pulse" />
                    </td>
                    <td className={TD_PADDING} />
                </tr>
            ))}
        </>
    );
}

function EmptyState({ onAddUser }: { onAddUser: () => void }) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm font-semibold text-gray-700">No users in this org yet</p>
            <button
                type="button"
                onClick={onAddUser}
                className="flex items-center gap-2 bg-black text-white font-semibold px-4 h-9 rounded-lg text-sm hover:bg-gray-900 transition-colors mt-1"
            >
                <PlusIcon className="h-4 w-4" />
                Add user
            </button>
        </div>
    );
}

// Same two-empty-states split as PracticeUsersTable: an org can genuinely
// have zero users, or a search can simply have no matches.
function NoSearchResultsState({
    searchValue,
    onClear
}: {
    searchValue: string;
    onClear: () => void;
}) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">No users match &ldquo;{searchValue}&rdquo;.</p>
            <button
                type="button"
                onClick={onClear}
                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm mt-1"
            >
                Clear search
            </button>
        </div>
    );
}

interface OrthodontistUsersTableProps {
    users: OrthodontistUserRow[];
    isLoading: boolean;
    /** True while `users` reflects a client-side filter with no matches - distinguishes
     *  a genuinely empty org from a search with no results (see OrthodontistUsersTab). */
    isSearching: boolean;
    search: DataTableSearchConfig;
    onAddUser: () => void;
    onRemove: (user: OrthodontistUserRow) => void;
}

export function OrthodontistUsersTable({
    users,
    isLoading,
    isSearching,
    search,
    onAddUser,
    onRemove
}: OrthodontistUsersTableProps) {
    const columns = useMemo<DataTableColumn<OrthodontistUserRow>[]>(
        () => [
            {
                key: 'user',
                label: 'User',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => (
                    <UserCell name={user.name} email={user.email} size={ROW_AVATAR_SIZE} />
                )
            },
            {
                key: 'membershipType',
                label: 'Membership',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => MEMBERSHIP_LABELS[user.membershipType]
            },
            {
                key: 'createdAt',
                label: 'Added',
                width: 'auto',
                cellClassName: `${TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
                headerClassName: `${TH_PADDING} whitespace-nowrap`,
                render: (user) => formatCreatedDate(user.createdAt)
            },
            actionsColumn<OrthodontistUserRow>({
                key: 'actions',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => (
                    <button
                        type="button"
                        onClick={() => onRemove(user)}
                        className="h-8 px-3 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                        Remove
                    </button>
                )
            })
        ],
        [onRemove]
    );

    const emptyState = useMemo(
        () =>
            isSearching ? (
                <NoSearchResultsState
                    searchValue={search.value}
                    onClear={() => search.onChange('')}
                />
            ) : (
                <EmptyState onAddUser={onAddUser} />
            ),
        [isSearching, onAddUser, search]
    );

    const actions = useMemo<DataTableAction[]>(
        () => [
            {
                key: 'add',
                label: 'Add user',
                icon: <PlusIcon className="h-4 w-4" />,
                variant: 'primary',
                onClick: onAddUser
            }
        ],
        [onAddUser]
    );

    return (
        <DataTable
            label="Org users"
            columns={columns}
            rows={users}
            getRowKey={(user) => user.id}
            tableLayout="auto"
            rowHeightClassName="h-16"
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={emptyState}
            emptyStateCellClassName="px-3 py-16"
            search={search}
            actions={actions}
            className="pb-8 overflow-hidden"
        />
    );
}
